import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { PremiumFooter } from '@/components/premium/PremiumFooter';
import { SEO } from '@/components/SEO';
import { Calendar, Clock, ArrowLeft, Linkedin, Twitter, Facebook, Loader2 } from 'lucide-react';
import { RelatedPages } from '@/components/RelatedPages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      setError(false);
      const [postRes, allRes] = await Promise.all([
        fetch(`${API_URL}/api/blog/${slug}`),
        fetch(`${API_URL}/api/blog`)
      ]);
      if (postRes.ok) {
        const data = await postRes.json();
        setPost(data);
        if (allRes.ok) {
          const all = await allRes.json();
          setRelatedPosts(all.filter(p => p.slug !== slug).slice(0, 3));
        }
      } else {
        setError(true);
      }
    } catch (err) {
      console.error('Error fetching blog post:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <PremiumHeader />
        <div className="container mx-auto px-8 py-32 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#c5a059]" />
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <PremiumHeader />
        <div className="container mx-auto px-8 py-20 text-center">
          <h1 className="text-4xl font-bold text-[#1a1a1a] mb-4" >Article Not Found</h1>
          <p className="text-gray-500 mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/blog" className="text-[#c5a059] font-bold hover:underline">Back to Blog</Link>
        </div>
      </div>
    );
  }

  const shareUrl = `https://www.modularfurniturestore.com/blog/${slug}`;
  const imageAlt = post.image_alt || post.title;

  // Article Schema JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.image_url,
    "author": { "@type": "Organization", "name": "Modular Furniture Store by Ezeefit" },
    "publisher": {
      "@type": "Organization",
      "name": "Modular Furniture Store",
      "logo": { "@type": "ImageObject", "url": "https://www.modularfurniturestore.com/mfs-logo.png" }
    },
    "datePublished": post.created_at,
    "dateModified": post.updated_at || post.created_at,
    "mainEntityOfPage": { "@type": "WebPage", "@id": shareUrl },
    "keywords": post.keywords || post.category
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <SEO 
        title={`${post.title} | Modular Furniture Store Blog`}
        description={post.excerpt}
        keywords={post.keywords || `${post.category}, office furniture`}
        canonical={`/blog/${slug}`}
        ogImage={post.image_url}
        ogType="article"
      />

      {/* Article Schema Markup */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />

      <PremiumHeader />
      
      {/* Hero Image */}
      <div className="pt-24 bg-[#f8f7f4]">
        <div className="container mx-auto px-8 py-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-gray-500 hover:text-[#c5a059] mb-6 text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
          <div className="max-w-4xl">
            <span className="text-[#c5a059] text-xs font-semibold uppercase tracking-[0.2em]">
              {post.category || post.theme}
            </span>
            <h1 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1a1a1a] mt-3 mb-6 leading-tight"
              
            >
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400 mb-8">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {post.read_time}
              </span>
              <span className="text-gray-500">By {post.author}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Image */}
      <div className="container mx-auto px-8">
        <img 
          src={post.image_url} 
          alt={imageAlt}
          className="w-full max-w-4xl h-64 md:h-96 object-cover"
          loading="lazy"
        />
      </div>

      {/* Content + Sidebar Layout */}
      <div className="container mx-auto px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <article className="lg:col-span-2">
            <div 
              className="prose prose-lg max-w-none
                prose-headings:font-bold prose-headings:text-[#1a1a1a]
                prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:pb-2 prose-h2:border-b prose-h2:border-gray-200
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-gray-600 prose-p:leading-relaxed
                prose-li:text-gray-600
                prose-strong:text-[#1a1a1a]
                prose-a:text-[#c5a059] prose-a:no-underline hover:prose-a:underline
                prose-table:text-sm prose-table:border-collapse
                prose-th:bg-[#f8f7f4] prose-th:p-3 prose-th:text-left prose-th:font-bold prose-th:text-[#1a1a1a] prose-th:border prose-th:border-gray-200
                prose-td:p-3 prose-td:border prose-td:border-gray-200
              "
              style={{ fontFamily: 'Manrope, sans-serif' }}
              dangerouslySetInnerHTML={{ __html: formatContent(post.content) }}
            />

            {/* Share Section */}
            <div className="border-t border-gray-200 pt-8 mt-12">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <span className="font-bold text-sm text-[#1a1a1a] uppercase tracking-wider">Share this article</span>
                <div className="flex items-center gap-3">
                  <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="p-2.5 bg-[#0077B5] text-white hover:opacity-80 transition-opacity" aria-label="Share on LinkedIn"><Linkedin className="w-4 h-4" /></a>
                  <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`} target="_blank" rel="noopener noreferrer" className="p-2.5 bg-[#1DA1F2] text-white hover:opacity-80 transition-opacity" aria-label="Share on Twitter"><Twitter className="w-4 h-4" /></a>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="p-2.5 bg-[#4267B2] text-white hover:opacity-80 transition-opacity" aria-label="Share on Facebook"><Facebook className="w-4 h-4" /></a>
                </div>
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-[120px] space-y-8">
              {/* CTA Card */}
              <div className="bg-[#f8f7f4] p-6 border border-gray-200">
                <h3 className="font-bold text-[#1a1a1a] mb-3" >
                  Need Expert Advice?
                </h3>
                <p className="text-gray-500 text-sm mb-4">Get a free office layout design and furniture quote.</p>
                <Link to="/bulk-inquiry" className="block w-full bg-[#c5a059] hover:bg-[#b8934d] text-[#1a1a1a] text-center py-3 font-bold text-sm uppercase tracking-wider transition-colors">
                  Get Free Quote
                </Link>
                <a href="tel:+919594685818" className="block w-full border border-[#1a1a1a] text-[#1a1a1a] text-center py-3 font-bold text-sm uppercase tracking-wider mt-3 hover:bg-[#1a1a1a] hover:text-white transition-colors">
                  Call 9594685818
                </a>
              </div>

              {/* Related Articles */}
              {relatedPosts.length > 0 && (
                <div>
                  <h3 className="font-bold text-[#1a1a1a] mb-4 uppercase text-sm tracking-wider">Related Articles</h3>
                  <div className="space-y-4">
                    {relatedPosts.map(rp => (
                      <Link key={rp.id} to={`/blog/${rp.slug}`} className="group block border border-gray-200 hover:border-[#c5a059]/50 transition-all overflow-hidden">
                        <img src={rp.image_url} alt={rp.image_alt || rp.title} className="w-full h-32 object-cover" loading="lazy" />
                        <div className="p-4">
                          <span className="text-[10px] text-[#c5a059] font-semibold uppercase tracking-wider">{rp.category}</span>
                          <h4 className="text-sm font-bold text-[#1a1a1a] mt-1 group-hover:text-[#c5a059] transition-colors line-clamp-2">
                            {rp.title}
                          </h4>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Browse Products */}
              <div className="border border-gray-200 p-4">
                <h3 className="font-bold text-[#1a1a1a] mb-3 text-sm uppercase tracking-wider">Browse Products</h3>
                <div className="space-y-2">
                  <Link to="/products?category=Modular%20Office%20Workstation" className="block text-sm text-gray-600 hover:text-[#c5a059] transition-colors py-1">Modular Workstations →</Link>
                  <Link to="/products?category=Privacy%20Booths" className="block text-sm text-gray-600 hover:text-[#c5a059] transition-colors py-1">Privacy Booths →</Link>
                  <Link to="/products?category=Storage%20Cabinets" className="block text-sm text-gray-600 hover:text-[#c5a059] transition-colors py-1">Storage Cabinets →</Link>
                </div>
              </div>

              {/* More Links */}
              <div className="border border-gray-200 p-4">
                <h3 className="font-bold text-[#1a1a1a] mb-3 text-sm uppercase tracking-wider">Useful Resources</h3>
                <div className="space-y-2">
                  <Link to="/pricing-guide" className="block text-sm text-gray-600 hover:text-[#c5a059] transition-colors py-1">Pricing Guide →</Link>
                  <Link to="/comparison-guide" className="block text-sm text-gray-600 hover:text-[#c5a059] transition-colors py-1">Comparison Guide →</Link>
                  <Link to="/legacy" className="block text-sm text-gray-600 hover:text-[#c5a059] transition-colors py-1">Our Legacy →</Link>
                  <Link to="/projects" className="block text-sm text-gray-600 hover:text-[#c5a059] transition-colors py-1">Delivered Workspaces →</Link>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Blog Post Interlinks */}
      <div className="container mx-auto px-4 sm:px-8 py-12 bg-white">
        <RelatedPages pageKey="blogPost" />
      </div>

      <PremiumFooter />
    </div>
  );
}

function formatContent(content) {
  if (!content) return '';
  return content
    .replace(/^## (.*$)/gim, '<h2>$1</h2>')
    .replace(/^### (.*$)/gim, '<h3>$1</h3>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/^\- (.*$)/gim, '<li>$1</li>')
    .replace(/\n\n/g, '</p><p>');
}
